import { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useNavigate } from "react-router-dom";
import type { EmailOtpType } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { chooseLandingPath } from "@/lib/auth-routing";
import { Sentry } from "@/lib/sentry";
import { toast } from "sonner";
import { stashTeamInviteTokenFromUrl, acceptPendingTeamInvite } from "@/lib/team-invite";
import { trackEvent } from "@/lib/posthog";
import { trackPixel } from "@/lib/meta-pixel";
import { trackGoogleAdsConversion } from "@/lib/google-ads";

/**
 * Signup.tsx tracks `user_signed_up` for the email/password flow at submit
 * time. OAuth signups (Google, etc.) never pass through that code, so this is
 * their only chance to fire it — right after the company row is created for
 * a brand-new account. Guard on provider so an email user completing this
 * same callback doesn't get double-counted against Signup.tsx's fire.
 */
function trackOAuthSignup(user: { id?: string; app_metadata?: { provider?: string } }, extras: { hasReferral: boolean }) {
  const provider = user.app_metadata?.provider;
  if (!provider || provider === "email") return;
  trackEvent("user_signed_up", {
    method: provider,
    has_referral: extras.hasReferral,
    has_invite: Boolean(localStorage.getItem("cr-invite-token")),
    requires_verification: false,
  });
  // Meta Pixel signup conversion (OAuth path) — parity with the email path in
  // Signup.tsx, which OAuth signups never traverse. Keyed by the Supabase user
  // id so a future server-side CAPI fire dedupes against it (matches Signup.tsx).
  // No-ops without analytics consent.
  trackPixel(
    "CompleteRegistration",
    { method: provider, status: true },
    user.id ? { eventID: user.id } : undefined,
  );
  // Google Ads sign-up conversion (OAuth path). The email/password path fires
  // this in Signup.tsx; OAuth signups only reach here, so this is their fire.
  // Guarded above to brand-new non-email accounts, so logins don't convert.
  // No-ops without analytics consent.
  trackGoogleAdsConversion();
}

/**
 * Map raw Supabase auth-callback errors (expired/invalid links, PKCE code
 * verifier mismatches, etc.) onto friendly copy. The raw message is sent to
 * Sentry only — users should never see API internals on the error screen.
 */
function friendlyCallbackError(raw: string | undefined | null): string {
  const msg = (raw ?? "").toLowerCase();
  if (msg.includes("code verifier") || msg.includes("pkce") || msg.includes("flow state")) {
    return "We couldn't finish signing you in — this link was opened in a different browser than the one you started in. Open it on the original device, or request a new one.";
  }
  if (/(expired|invalid)/.test(msg) && /(link|token|code|otp)/.test(msg)) {
    return "This link has expired or has already been used. Request a new one and try again.";
  }
  return "We couldn't finish signing you in — the link may have expired. Request a new one.";
}

/**
 * Report the raw callback error to Sentry, then return the friendly copy. An
 * expired / already-used / invalid email link is a normal user condition — the
 * error screen already routes them to request a new one — not an app fault, so
 * we skip Sentry for that recognized case to keep error tracking signal-only
 * (was noise: a user retrying an expired link logged N warnings). PKCE /
 * code-verifier mismatches and anything unrecognized are still captured.
 */
function reportCallbackError(raw: string | undefined | null, stage: string): string {
  const msg = (raw ?? "").toLowerCase();
  const isExpectedLinkExpiry =
    !/(verifier|pkce|flow state)/.test(msg) &&
    (/expired/.test(msg) || /already (been )?used/.test(msg) ||
      (/invalid/.test(msg) && /(link|otp|email|token|code)/.test(msg)));
  if (!isExpectedLinkExpiry) {
    Sentry.captureMessage("auth callback failed", {
      level: "warning",
      tags: { source: "auth-callback", stage },
      extra: { message: raw ?? "unknown" },
    });
  }
  return friendlyCallbackError(raw);
}

/**
 * Accept a pending team invite (stashed in localStorage from
 * /auth?team_invite=<token> — see src/lib/team-invite.ts) now that the user
 * is signed in. Mirrors the carrier-invite accept below: non-blocking, the
 * user always continues to the dashboard; we just toast the outcome.
 */
async function acceptTeamInviteIfStashed(flow: "email" | "oauth"): Promise<void> {
  try {
    const result = await acceptPendingTeamInvite();
    if (result.status === "accepted") {
      toast.success("Team invite accepted — you've joined the team.");
    } else if (result.status === "error") {
      toast.error(`Could not accept the team invite: ${result.message}`);
      Sentry.captureMessage("team-invite accept failed", {
        level: "warning",
        tags: { source: "team-invite.accept", flow },
        extra: { message: result.message, code: result.code },
      });
    }
  } catch (e) {
    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
      level: "warning",
      tags: { source: "team-invite.accept", flow },
    });
  }
}

/**
 * If the just-authenticated user has a verified TOTP factor, route them to
 * /mfa-verify before the dashboard. Returns true if a redirect was issued so
 * the caller can short-circuit. Fails closed, listFactors errors are treated
 * as "do not let them in" rather than letting OAuth users skip MFA silently.
 */
async function redirectIfMfaRequired(navigate: (path: string, opts?: { replace?: boolean }) => void): Promise<boolean> {
  try {
    const { data: factors, error: factorsErr } = await supabase.auth.mfa.listFactors();
    if (factorsErr) throw factorsErr;
    const hasVerifiedTotp = (factors?.totp ?? []).some(f => f.status === "verified");
    if (hasVerifiedTotp) {
      navigate("/mfa-verify", { replace: true });
      return true;
    }
    return false;
  } catch (e) {
    console.error("MFA factor check failed during auth callback:", e);
    await supabase.auth.signOut().catch(() => {});
    navigate("/login", { replace: true });
    return true;
  }
}

export default function AuthCallback() {
  useDocumentTitle("Authenticating");
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const handleCallback = async () => {
      try {
        // Edge case: the user lands here with ?team_invite=<token> directly
        // (already-authenticated invitee clicking the email link). Stash it so
        // the accept below picks it up; normally Login.tsx stashed it earlier.
        stashTeamInviteTokenFromUrl(window.location.search);

        // Native email-confirmation path: the signup/email-change templates
        // point at /auth/confirm?token_hash=...&type=... on our own domain
        // rather than Supabase's /auth/v1/verify GET URL (which corporate email
        // scanners prefetch and consume, so the user's click hits otp_expired).
        // Redeem the token_hash via verifyOtp to establish the session, then
        // fall through to the normal company-setup / routing below.
        const params = new URLSearchParams(window.location.search);
        const tokenHash = params.get("token_hash");
        if (tokenHash) {
          const otpType = (params.get("type") as EmailOtpType | null) ?? "email";
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpType,
          });
          if (verifyError) { setError(reportCallbackError(verifyError.message, "verify_otp")); return; }

          // Recovery and email-change links establish a session but must land on
          // a specific page (set a new password / return to settings), not the
          // signup onboarding flow. Honor the template's ?next=, but only if it's
          // a safe same-origin relative path (guards against open-redirect).
          if (otpType === "recovery" || otpType === "email_change") {
            const next = params.get("next");
            const safeNext = next && next.startsWith("/") && !next.startsWith("//")
              ? next
              : otpType === "recovery" ? "/reset-password" : "/dash";
            navigate(safeNext, { replace: true });
            return;
          }
        }

        const code = new URLSearchParams(window.location.search).get("code");
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) { setError(reportCallbackError(exchangeError.message, "exchange_code")); return; }
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) { setError(reportCallbackError(sessionError.message, "get_session")); return; }

        if (session?.user) {
          // Check both owner and team-member paths so users invited to an
          // existing company don't trigger duplicate company creation.
          const { data: ownedCompany } = await supabase
            .from("companies").select("id").eq("owner_user_id", session.user.id).maybeSingle();
          let existingCompany = ownedCompany;
          if (!existingCompany) {
            const { data: roleRow } = await supabase
              .from("company_roles")
              .select("company_id")
              .eq("user_id", session.user.id)
              .not("accepted_at", "is", null)
              .order("accepted_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (roleRow?.company_id) existingCompany = { id: roleRow.company_id };
          }

          if (!existingCompany) {
            const pendingCompany = localStorage.getItem("cr-pending-signup-company");
            const pendingReferral = localStorage.getItem("cr-pending-referral-code");
            const pendingCaBrokerRaw = localStorage.getItem("cr-pending-ca-broker");
            const companyName =
              session.user.user_metadata?.company_name || pendingCompany || "My Company";
            const vertical = session.user.user_metadata?.vertical || "freight";
            // Role comes from metadata when a dedicated door set it
            // (/carriers/signup, /brokers/signup). Generic /signup has no
            // role yet — pass NONE and let the role picker set it, instead of
            // the old "dispatcher" default that permanently mis-roled
            // picker-abandoners AND broke setup-account's null-only role
            // backfill for the picker's edge-function fallback (2026-07-14
            // Wave 2, red-team finding).
            const metaRole = session.user.user_metadata?.logistics_role as string | undefined;
            // CA broker payload: localStorage primary, user_metadata fallback.
            // Parse defensively so bad JSON never blocks signup.
            let caBrokerData: Record<string, unknown> | undefined;
            if (pendingCaBrokerRaw) {
              try {
                const parsed = JSON.parse(pendingCaBrokerRaw);
                if (parsed && typeof parsed === "object") caBrokerData = parsed;
              } catch { /* ignore */ }
            }
            if (!caBrokerData && session.user.user_metadata?.ca_broker_data) {
              caBrokerData = session.user.user_metadata.ca_broker_data as Record<string, unknown>;
            }
            const { error: setupError } = await supabase.functions.invoke("setup-account", {
              body: {
                user_id: session.user.id,
                company_name: companyName,
                vertical,
                ...(metaRole ? { logistics_role: metaRole } : {}),
                ...(pendingReferral ? { referral_code: pendingReferral } : {}),
                ...(caBrokerData ? { country: "CA", ca_broker_data: caBrokerData } : {}),
              },
            });
            if (setupError) {
              setError(`Could not finish account setup: ${setupError.message ?? "unknown error"}. Try signing in again, or contact support@cipherandrow.com.`);
              return;
            }
            localStorage.removeItem("cr-pending-signup-company");
            localStorage.removeItem("cr-pending-referral-code");
            localStorage.removeItem("cr-pending-ca-broker");
            trackOAuthSignup(session.user, { hasReferral: Boolean(pendingReferral) });
          }

          // Accept carrier invite if stored
          const pendingInvite = localStorage.getItem("cr-invite-token");
          const inviteExpiry = localStorage.getItem("cr-invite-token-expiry");
          if (pendingInvite) {
            localStorage.removeItem("cr-invite-token");
            localStorage.removeItem("cr-invite-token-expiry");
            const isExpired = inviteExpiry && Date.now() > Number(inviteExpiry);
            if (!isExpired) {
              try {
                // functions.invoke resolves (not throws) on 4xx — read the
                // error so expired/mismatched-email accepts stop vanishing.
                const { error: acceptError } = await supabase.functions.invoke("carrier-invite", {
                  body: { action: "accept", token: pendingInvite },
                });
                if (acceptError) {
                  toast.error("We couldn't link you to the company that invited you. Ask them to re-send the invite (it may have expired or used a different email).");
                  Sentry.captureMessage("carrier-invite accept rejected", {
                    level: "warning",
                    tags: { source: "carrier-invite.accept", flow: "email" },
                  });
                }
              } catch (e) {
                Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
                  level: "warning",
                  tags: { source: "carrier-invite.accept", flow: "email" },
                });
              }
            }
          }

          // Accept team invite if stashed (joins the inviter's company)
          await acceptTeamInviteIfStashed("email");

          if (await redirectIfMfaRequired(navigate)) return;

          // Verify-funnel deep link: a visitor who clicked "Reveal — free" on a
          // public scan result lands straight on the authenticated result for
          // that carrier instead of the generic onboarding path, so the unlock
          // actually pays off. Only fires when the public page set this key, so
          // normal signups are unaffected. The path guard blocks protocol-
          // relative / external open-redirects.
          const verifyRedirect = localStorage.getItem("cr-post-auth-redirect");
          if (verifyRedirect) {
            localStorage.removeItem("cr-post-auth-redirect");
            localStorage.removeItem("cr-needs-role-pick");
            if (/^\/(?!\/)/.test(verifyRedirect)) {
              navigate(verifyRedirect, { replace: true });
              return;
            }
          }

          // cr-needs-role-pick is set by Signup.tsx (email + Google OAuth paths)
          // when the user comes from the generic /signup page. It survives the
          // email verification round-trip because it's stored in the same browser.
          // Carrier/broker dedicated signup pages never set it, so those users
          // skip the picker and go straight to chooseLandingPath.
          const needsRolePick = localStorage.getItem("cr-needs-role-pick");
          if (needsRolePick) {
            localStorage.removeItem("cr-needs-role-pick");
            navigate("/signup/choose-role", { replace: true });
            return;
          }

          const role = session.user.user_metadata?.logistics_role ?? null;
          const path = await chooseLandingPath(session.user.id, role);
          navigate(path, { replace: true });
        } else {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (event === "SIGNED_IN" && sess?.user) {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              subscription.unsubscribe();
              subscriptionRef.current = null;
              if (cancelledRef.current) return;

              const { data: ownedExisting } = await supabase
                .from("companies").select("id").eq("owner_user_id", sess.user.id).maybeSingle();
              let existing = ownedExisting;
              if (!existing) {
                const { data: roleRow } = await supabase
                  .from("company_roles")
                  .select("company_id")
                  .eq("user_id", sess.user.id)
                  .not("accepted_at", "is", null)
                  .order("accepted_at", { ascending: false })
                  .limit(1)
                  .maybeSingle();
                if (roleRow?.company_id) existing = { id: roleRow.company_id };
              }

              if (!existing) {
                const pendingCompany = localStorage.getItem("cr-pending-signup-company");
                const pendingReferral = localStorage.getItem("cr-pending-referral-code");
                const pendingCaBrokerRaw = localStorage.getItem("cr-pending-ca-broker");
                const companyName =
                  sess.user.user_metadata?.company_name || pendingCompany || "My Company";
                const vertical = sess.user.user_metadata?.vertical || "freight";
                // Same rule as the PKCE branch (2026-07-14 Wave 2): only pass
                // a role the user actually chose; the picker owns the rest.
                const metaOauthRole = sess.user.user_metadata?.logistics_role as string | undefined;
                let caBrokerData: Record<string, unknown> | undefined;
                if (pendingCaBrokerRaw) {
                  try {
                    const parsed = JSON.parse(pendingCaBrokerRaw);
                    if (parsed && typeof parsed === "object") caBrokerData = parsed;
                  } catch { /* ignore */ }
                }
                if (!caBrokerData && sess.user.user_metadata?.ca_broker_data) {
                  caBrokerData = sess.user.user_metadata.ca_broker_data as Record<string, unknown>;
                }
                const { error: setupError } = await supabase.functions.invoke("setup-account", {
                  body: {
                    user_id: sess.user.id,
                    company_name: companyName,
                    vertical,
                    ...(metaOauthRole ? { logistics_role: metaOauthRole } : {}),
                    ...(pendingReferral ? { referral_code: pendingReferral } : {}),
                    ...(caBrokerData ? { country: "CA", ca_broker_data: caBrokerData } : {}),
                  },
                });
                if (setupError) {
                  setError(`Could not finish account setup: ${setupError.message ?? "unknown error"}. Try signing in again, or contact support@cipherandrow.com.`);
                  return;
                }
                localStorage.removeItem("cr-pending-signup-company");
                localStorage.removeItem("cr-pending-referral-code");
                localStorage.removeItem("cr-pending-ca-broker");
                trackOAuthSignup(sess.user, { hasReferral: Boolean(pendingReferral) });
              }

              const oauthInvite = localStorage.getItem("cr-invite-token");
              const oauthInviteExpiry = localStorage.getItem("cr-invite-token-expiry");
              if (oauthInvite) {
                localStorage.removeItem("cr-invite-token");
                localStorage.removeItem("cr-invite-token-expiry");
                const isExpired = oauthInviteExpiry && Date.now() > Number(oauthInviteExpiry);
                if (!isExpired) {
                  try {
                    // functions.invoke resolves (not throws) on 4xx — read the
                    // error so expired/mismatched-email accepts stop vanishing.
                    const { error: acceptError } = await supabase.functions.invoke("carrier-invite", {
                      body: { action: "accept", token: oauthInvite },
                    });
                    if (acceptError) {
                      toast.error("We couldn't link you to the company that invited you. Ask them to re-send the invite (it may have expired or used a different email).");
                      Sentry.captureMessage("carrier-invite accept rejected", {
                        level: "warning",
                        tags: { source: "carrier-invite.accept", flow: "oauth" },
                      });
                    }
                  } catch (e) {
                    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
                      level: "warning",
                      tags: { source: "carrier-invite.accept", flow: "oauth" },
                    });
                  }
                }
              }

              // Accept team invite if stashed (joins the inviter's company)
              await acceptTeamInviteIfStashed("oauth");

              if (await redirectIfMfaRequired(navigate)) return;

              // Verify-funnel deep link (mirrors the email branch above): land an
              // OAuth signup on the authed result for the carrier they revealed.
              // Internal-path-guarded; only fires when the public page set the key.
              const oauthVerifyRedirect = localStorage.getItem("cr-post-auth-redirect");
              if (oauthVerifyRedirect) {
                localStorage.removeItem("cr-post-auth-redirect");
                localStorage.removeItem("cr-needs-role-pick");
                if (/^\/(?!\/)/.test(oauthVerifyRedirect)) {
                  navigate(oauthVerifyRedirect, { replace: true });
                  return;
                }
              }

              const oauthNeedsRolePick = localStorage.getItem("cr-needs-role-pick");
              if (oauthNeedsRolePick) {
                localStorage.removeItem("cr-needs-role-pick");
                navigate("/signup/choose-role", { replace: true });
                return;
              }

              const oauthRoleForRedirect =
                sess.user.user_metadata?.logistics_role ?? null;
              const oauthPath = await chooseLandingPath(sess.user.id, oauthRoleForRedirect);
              navigate(oauthPath, { replace: true });
            }
          });
          subscriptionRef.current = subscription;

          timeoutRef.current = setTimeout(() => {
            if (cancelledRef.current) return;
            setError("Verification timed out. Please try signing in.");
          }, 10000);
        }
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        setError(reportCallbackError(err instanceof Error ? err.message : String(err), "unexpected"));
      }
    };

    handleCallback();
    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-sm">
          <h1 className="text-2xl font-display text-foreground">Verification Failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex flex-col gap-2">
            <a href="/login" className="text-primary text-sm hover:underline">Go to Sign In →</a>
            <a href="/verify-email" className="text-primary text-sm hover:underline">Request a new verification email →</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-background flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Verifying your account…</p>
      </div>
    </div>
  );
}
